import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by navigating to the selected variant media.
   * If the incoming section has a different media set, fall back to replacing the gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const selectedMediaId = this.#getSelectedMediaId(newMediaGallery);

        if (selectedMediaId && this.#hasSameMediaOrder(newMediaGallery) && this.#navigateToMedia(selectedMediaId)) {
          return;
        }

        await this.#waitForMediaGalleryReady(newMediaGallery);

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Gets the selected variant media id from the incoming section HTML.
   * Fabric renders the selected variant media first, so we use that as a signal
   * without letting the new order replace the current gallery.
   *
   * @param {Element} mediaGallery - The incoming media gallery from section-rendered HTML.
   * @returns {string | undefined}
   */
  #getSelectedMediaId(mediaGallery) {
    return mediaGallery.querySelector('.product-media[data-media-id]')?.getAttribute('data-media-id') || undefined;
  }

  /**
   * Compares media IDs between the current and incoming galleries.
   *
   * @param {Element} mediaGallery - The incoming media gallery from section-rendered HTML.
   * @returns {boolean}
   */
  #hasSameMediaOrder(mediaGallery) {
    const currentIds = this.#getMediaIds(this).sort();
    const incomingIds = this.#getMediaIds(mediaGallery).sort();

    return currentIds.length > 0 && currentIds.length === incomingIds.length && currentIds.every((id, index) => id === incomingIds[index]);
  }

  /**
   * Gets unique media IDs for a gallery.
   *
   * @param {Element} mediaGallery - The media gallery to inspect.
   * @returns {string[]}
   */
  #getMediaIds(mediaGallery) {
    return [...mediaGallery.querySelectorAll('.product-media[data-media-id]')]
      .map((element) => element.getAttribute('data-media-id'))
      .filter((id, index, ids) => Boolean(id) && ids.indexOf(id) === index);
  }

  /**
   * Moves the gallery view to an existing media card without changing DOM order.
   *
   * @param {string} mediaId - The selected media ID.
   * @returns {boolean}
   */
  #navigateToMedia(mediaId) {
    const media = this.querySelector(`.product-media[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!media) return false;

    const slide = media.closest('slideshow-slide');
    const gridItem = media.closest('.media-gallery__grid > li');
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (slide instanceof HTMLElement && this.slideshow?.refs.slides?.length) {
      const index = this.slideshow.refs.slides.indexOf(slide);

      slide.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });

      if (index >= 0) {
        this.slideshow.current = index;
        this.slideshow.refs.slides.forEach((item, itemIndex) => {
          item.setAttribute('aria-hidden', `${itemIndex !== index}`);
        });
      }
    }

    if (gridItem instanceof HTMLElement && getComputedStyle(gridItem).display !== 'none') {
      gridItem.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
    }

    return true;
  }

  /**
   * Keeps the current gallery visible until the incoming variant's first image is ready to paint.
   * This avoids a small blank flash when switching variants on image-heavy product pages.
   *
   * @param {Element} mediaGallery - The incoming media gallery from the section-rendered HTML.
   * @returns {Promise<void>}
   */
  async #waitForMediaGalleryReady(mediaGallery) {
    const image = /** @type {HTMLImageElement | null} */ (mediaGallery.querySelector('img'));
    if (!image) return;

    const source = image.currentSrc || image.src || image.getAttribute('src');
    if (!source) return;

    const preload = new Image();
    const sizes = image.getAttribute('sizes');
    const srcset = image.getAttribute('srcset');

    if (sizes) preload.sizes = sizes;
    if (srcset) preload.srcset = srcset;
    preload.src = source;

    if (preload.complete) return;

    const loaded = preload.decode
      ? preload.decode().catch(() => undefined)
      : new Promise((resolve) => {
          preload.onload = resolve;
          preload.onerror = resolve;
        });

    await Promise.race([
      loaded,
      new Promise((resolve) => setTimeout(resolve, 700)),
    ]);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
